import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
    ApiClientService,
    AuthenticationService,
    CachedValuesService,
    PermissionService,
    ToastService,
    RightPanelService
} from '@services/index';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, filter, map, Observable, take } from 'rxjs';
import { UserForm } from './create-and-update-user-modal.model';
import { CreateUserResponse, UpdateUserResponse } from '@services/api-client/responses';
import { Permission, Role, ValidationMessageKey } from 'app/enums';
import { matchValidator, passwordValidator } from '@helpers/index';
import { Customer, User } from 'app/types';
import { DropdownItem } from '@shared/components/dropdown/dropdown.component';
import { CustomFormModule } from '@shared/custom-form/custom-form.module';
import { SharedModule } from '@shared/shared.module';
import { ErrorHelper } from '@helpers/error-helper';

@UntilDestroy()
@Component({
    selector: 'app-create-and-update-user-modal',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, TranslateModule, CustomFormModule, SharedModule],
    templateUrl: './create-and-update-user-modal.component.html',
    styleUrl: './create-and-update-user-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateAndUpdateUserModalComponent implements OnInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly translateService = inject(TranslateService);
    private readonly toastService = inject(ToastService);
    private readonly apiClientService = inject(ApiClientService);
    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly permissionService = inject(PermissionService);
    private readonly rightPanelService = inject(RightPanelService);

    @Input() public editUser?: User;

    public showPotentialValidationMessages = true;
    public loading: boolean = false;
    public userForm: FormGroup<UserForm>;
    public roles = Role;
    public isEditMode = false;
    private hasAdminRole = this.permissionService.hasAnyPermission([Permission.WriteUser]);

    private readonly dataChangedSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
    public dataChangedUser$: Observable<User> = this.dataChangedSubject.asObservable().pipe(
        filter((user) => user != null),
        map((user) => user)
    );

    public readonly customers$: Observable<DropdownItem<string>[]> = this.cachedValuesService.customers$.pipe(
        map((data) =>
            data.map((item: Customer) => {
                return {
                    value: item.id,
                    label: item.name
                } as DropdownItem<string>;
            })
        )
    );

    public get f() {
        return this.userForm.controls;
    }

    public get isMyUser() {
        return this.editUser?.id === this.authenticationService.getUser().userId;
    }

    public ngOnInit() {
        const isCreate = !this.editUser;
        const fb = new FormBuilder();
        this.userForm = fb.nonNullable.group({
            firstName: [this.editUser?.firstName || '', [Validators.required]],
            lastName: [this.editUser?.lastName || '', [Validators.required]],
            email: [this.editUser?.email || '', [Validators.required, Validators.email]],
            title: [this.editUser?.title || ''],
            phone: [this.editUser?.phone || ''],
            customerId: [this.editUser?.customerId || '', [Validators.required]],
            password: ['', isCreate ? [Validators.required] : []],
            confirmPassword: ['', isCreate ? [Validators.required] : []],
            inboxEmail: [this.editUser?.inboxEmail || ''],
            roles: [this.editUser?.roles || '' || []]
        });

        // Set cross-field validators only if needed
        const setPasswordValidators = () => {
            const password = this.f.password.value;
            const confirmPassword = this.f.confirmPassword.value;
            const shouldValidate = isCreate || password || confirmPassword;

            if (shouldValidate) {
                this.userForm.setValidators([
                    matchValidator('password', 'confirmPassword', ValidationMessageKey.PasswordsMismatch),
                    passwordValidator('password', ValidationMessageKey.WeakPassword)
                ]);
                this.f.password.setValidators(isCreate ? [Validators.required] : []);
                this.f.confirmPassword.setValidators(isCreate ? [Validators.required] : []);
            } else {
                this.userForm.clearValidators();
                this.f.password.clearValidators();
                this.f.confirmPassword.clearValidators();
            }
            this.f.password.updateValueAndValidity({ emitEvent: false });
            this.f.confirmPassword.updateValueAndValidity({ emitEvent: false });
            this.userForm.updateValueAndValidity({ emitEvent: false });
        };

        // Listen for changes to password fields
        this.f.password.valueChanges.subscribe(() => setPasswordValidators());
        this.f.confirmPassword.valueChanges.subscribe(() => setPasswordValidators());

        setPasswordValidators();

        this.f.inboxEmail.disable();
        if (!this.hasAdminRole) {
            this.f.roles.disable();
        }

        // Start in readonly mode if editing existing user, otherwise start in edit mode
        this.isEditMode = !this.editUser;
    }

    public isDisabled() {
        return this.loading || !this.userForm.dirty;
    }

    public get customerName$(): Observable<string> {
        return this.customers$.pipe(
            map((customers: DropdownItem<string>[]) => {
                const customer = customers.find((c: DropdownItem<string>) => c.value === this.editUser?.customerId);
                return customer ? customer.label : '-';
            })
        );
    }

    public save() {
        if (this.userForm.dirty) {
            this.showPotentialValidationMessages = true;

            if (this.userForm.valid) {
                this.loading = true;
                this.changeDetectorRef.markForCheck();

                if (this.editUser) {
                    this.apiClientService
                        .updateUser$({
                            userId: this.editUser.id,
                            firstName: this.f.firstName.value,
                            lastName: this.f.lastName.value,
                            email: this.f.email.value,
                            title: this.f.title.value,
                            phone: this.f.phone.value,
                            customerId: this.f.customerId.value,
                            setPassword: this.f.password.value,
                            roles: this.f.roles.value
                        })
                        .pipe(untilDestroyed(this), take(1))
                        .subscribe({
                            next: (response: UpdateUserResponse) => {
                                const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                                const message = this.translateService.instant(
                                    'TOAST_MESSAGES.UPDATE_USER_SUCCESS'
                                ) as string;
                                this.cachedValuesService.invalidateCache();
                                this.dataChangedSubject.next(response);
                                this.toastService.open(title, message, { type: 'success' });
                                this.cachedValuesService.invalidateCache();
                                this.closePanel();
                            },
                            error: (error) => {
                                this.loading = false;
                                this.changeDetectorRef.markForCheck();

                                const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                                let message: string;

                                // Check if it's a validation error
                                if (ErrorHelper.isValidationError(error)) {
                                    message = this.translateService.instant(
                                        `VALIDATION_MESSAGES.${ErrorHelper.getErrorMessage(error)}`
                                    ) as string;
                                } else if (ErrorHelper.isConflictError(error)) {
                                    // Handle conflict errors (e.g., duplicate user)
                                    message = ErrorHelper.getErrorMessage(
                                        error,
                                        this.translateService.instant('TOAST_MESSAGES.UPDATE_USER_ERROR') as string
                                    );
                                } else {
                                    // Generic error fallback
                                    message = this.translateService.instant(
                                        'TOAST_MESSAGES.UPDATE_USER_ERROR'
                                    ) as string;
                                }

                                this.toastService.open(title, message, { type: 'error', progressBar: true });
                            }
                        });
                } else {
                    this.apiClientService
                        .createUser$({
                            firstName: this.f.firstName.value,
                            lastName: this.f.lastName.value,
                            email: this.f.email.value,
                            title: this.f.title.value,
                            phone: this.f.phone.value,
                            customerId: this.f.customerId.value,
                            setPassword: this.f.password.value,
                            roles: this.f.roles.value
                        })
                        .pipe(untilDestroyed(this), take(1))
                        .subscribe({
                            next: (response: CreateUserResponse) => {
                                const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                                const message = this.translateService.instant(
                                    'TOAST_MESSAGES.CREATE_USER_SUCCESS'
                                ) as string;
                                this.dataChangedSubject.next(response);
                                this.toastService.open(title, message, { type: 'success' });
                                this.cachedValuesService.invalidateCache();
                                this.closePanel();
                            },
                            error: (error) => {
                                this.loading = false;
                                this.changeDetectorRef.markForCheck();

                                const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                                let message: string;

                                // Check if it's a validation error
                                if (ErrorHelper.isValidationError(error)) {
                                    message = this.translateService.instant(
                                        `VALIDATION_MESSAGES.${ErrorHelper.getErrorMessage(error)}`
                                    ) as string;
                                } else if (ErrorHelper.isConflictError(error)) {
                                    // Handle conflict errors (e.g., duplicate email)
                                    message = ErrorHelper.getErrorMessage(
                                        error,
                                        this.translateService.instant('TOAST_MESSAGES.CREATE_USER_ERROR') as string
                                    );
                                } else {
                                    // Generic error fallback
                                    message = this.translateService.instant(
                                        'TOAST_MESSAGES.CREATE_USER_ERROR'
                                    ) as string;
                                }

                                this.toastService.open(title, message, { type: 'error', progressBar: true });
                            }
                        });
                }
            }
        }
    }

    public closePanel() {
        this.rightPanelService.hide();
    }

    public enterEditMode() {
        this.isEditMode = true;
        this.changeDetectorRef.markForCheck();
    }

    public cancelEdit() {
        this.isEditMode = false;
        this.showPotentialValidationMessages = false;
        // Reset form to original values
        if (this.editUser) {
            this.userForm.patchValue({
                firstName: this.editUser.firstName || '',
                lastName: this.editUser.lastName || '',
                email: this.editUser.email || '',
                title: this.editUser.title || '',
                phone: this.editUser.phone || '',
                customerId: this.editUser.customerId || '',
                password: '',
                confirmPassword: '',
                inboxEmail: this.editUser.inboxEmail || '',
                roles: this.editUser.roles || []
            });
            this.userForm.markAsPristine();
        } else {
            this.closePanel();
        }
        this.changeDetectorRef.markForCheck();
    }

    public saveAndExitEdit() {
        this.save();
        // The save method will call closePanel() on success, but if we want to stay in readonly mode:
        // this.isEditMode = false;
    }
}
